/**
 * PersonAvatar — renders a person's avatar image (or gravatar/initials fallback).
 * Props:
 *   avatarUrl  {string|null}  — direct image URL (null → gravatar/initial fallback)
 *   name       {string}       — person's display name
 *   email      {string|null}  — person's email (used for gravatar hash when no avatarUrl)
 *   size       {number}       — pixel size (default 32)
 *   showTooltip {boolean}     — wrap in tooltip div (default false in list context)
 *   cssClass   {string}       — additional CSS classes on the img
 */

import { h } from 'preact'

export function PersonAvatar({
  avatarUrl = null,
  name = '?',
  email = null,
  size = 32,
  showTooltip = false,
  cssClass = '',
}) {
  const initials = (() => {
    const fromName = (name || '').trim()
    if (fromName && fromName !== '?') return fromName[0].toUpperCase()
    const fromEmail = (email || '').trim()
    if (fromEmail) return fromEmail[0].toUpperCase()
    return '?'
  })()

  // Build src: prefer avatarUrl, fall back to gravatar
  let src = avatarUrl
  if (!src && email) {
    // Gravatar — client can't do MD5 easily without a lib; use a data: URI placeholder
    // or fall through to initials. We pass gravatar URL from the JSON anyway for real users.
    src = null
  }

  const imgClass = `rounded-circle border bg-white person-avatar-${size} ${cssClass}`.trim()

  const inner = src
    ? (
      <img
        src={src}
        width={size}
        height={size}
        class={imgClass}
        alt={name}
        loading="lazy"
        onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = '') }}
      />
    )
    : (
      <div
        class={`rounded-circle d-flex align-items-center justify-content-center bg-primary-subtle border ${cssClass}`.trim()}
        style={{ width: `${size}px`, height: `${size}px`, flexShrink: 0, fontSize: `${Math.round(size * 0.4)}px` }}
        aria-label={name}
      >
        <span class="fw-bold text-primary">{initials}</span>
      </div>
    )

  if (showTooltip) {
    return (
      <div
        data-bs-toggle="tooltip"
        data-bs-title={name}
        data-bs-placement="top"
        title={name}
      >
        {inner}
      </div>
    )
  }

  return inner
}
